import React, { Component, createRef } from 'react';
import { RouteComponentProps } from 'react-router';
import { ApiUrls } from '@api/apiUrls';
import { FIELDS_MASTER } from '@components-member/TradingAccounts/CopyTradingAccounts/MasterFormFields';
import { getCopyTradingSettings } from '@components-member/socialTrading/shared/requests';
import { getMasterRecommendedDepositTooltipMsg, setTooltipMessage } from '@components-member/socialTrading/utils';
import ErrorView, { IErrorViewModel } from '@components-shared/errorView';
import { IFormField } from '@components-shared/forms/formField.interface';
import { FormUtils } from '@components-shared/forms/forms.utils';
import { NotificationDialog } from '@components-shared/modal';
import Preloader from '@components-shared/preloader';
import { IAccount } from '@components-shared/shared.interfaces';
import { EmptyObject } from '@components-shared/shared.interfaces';
import { EnumUTACopyTAMasterFormField } from '@components-shared/socialTrading/enums';
import { IUTAMasterSettingsStatus, RecommendedMinDepositLimitsType } from '@components-shared/socialTrading/interfaces';
import {
  IUTAMAFormAccountNewSettings,
  IUTAMasterSettingsDetails,
  IUTAMasterSettingsForm,
} from '@components-shared/socialTrading/interfaces';
import { leverageFieldHandler, minDepositLimitsAreConfigured } from '@components-shared/socialTrading/utils';
import { keyMap } from '@locales/lokalise/keyMap';
import { fetchApi } from 'api/fetchApi';
import { IApiGroupMappingType } from 'api/interfaces/data-contracts';
import generator, { getInitialState } from 'components/shared/forms/formGenerator';
import { connectToContext, IAppContext } from 'contextStore';
import { handleError } from 'utils/errors';
import { IContext } from '../AccountCreate/interfaces';

interface IUTAMasterSettingsFormProps extends RouteComponentProps, IAppContext {
  masterId: number | null;
  groupMappingType: IApiGroupMappingType;
}

interface IUTAMasterSettingsFormState extends IErrorViewModel {
  form: IUTAMasterSettingsForm;
  account: IUTAMasterSettingsDetails | EmptyObject;
  profitSharingLimit: number;
  recommendedMinDepositAmount: number | null;
  recommendedMinDepositLimits: RecommendedMinDepositLimitsType[];
  isUpdateMasterLeverageAllowed: boolean;
}

const Form = generator(FIELDS_MASTER, {
  makeMinDepositAmountRequired: (ctx: IContext, value: boolean) => {
    const { makeDepositReqTrigger } = ctx.props;

    if (makeDepositReqTrigger) {
      makeDepositReqTrigger(value);
    }
  },
});

class MasterSettingsForm extends Component<IUTAMasterSettingsFormProps, IUTAMasterSettingsFormState> {
  formRef = createRef<{
    update: (data: Array<IFormField>) => void;
    resetValidation: () => void;
  }>();

  fields: IFormField[];

  constructor(props: IUTAMasterSettingsFormProps) {
    super(props);

    const fields: Array<IFormField> = [...FIELDS_MASTER];

    this.state = {
      account: {},
      form: { ...getInitialState(fields) },
      profitSharingLimit: 100,
      recommendedMinDepositAmount: null,
      recommendedMinDepositLimits: [],
      processing: true,
      errMsg: '',
      isUpdateMasterLeverageAllowed: false,
    };

    this.fields = [...FIELDS_MASTER];
  }

  async componentDidMount(): Promise<void> {
    await this.getDetails();
  }

  updateAccountData = async (updatePath: string, id: number, data: IUTAMAFormAccountNewSettings): Promise<void> => {
    try {
      await fetchApi.post<IAccount>(updatePath, data, { params: { id } });
      NotificationDialog.show(keyMap.tradingAccountsChangeSettingsSuccessMessage);
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToSaveSettings, error);
    }
  };

  updateMasterAccountData = (): Promise<void> => {
    const { form, account } = this.state;
    const updateData = { ...form };

    const { id } = account;

    delete updateData.accountType;
    delete updateData.commissionAccountLogin;

    updateData.copyType = account.copyType;
    updateData.isVisible = this.findMasterStatusValue();
    updateData.commissionAccountId = this.findCommissionAccountId();
    updateData.profitSharing = +updateData.profitSharing;

    return this.updateAccountData(ApiUrls.tradingAccountUser.updateCopyTradingMasterAccount, id, updateData);
  };

  getDetails = async (): Promise<void> => {
    const { masterId } = this.props;

    try {
      const [accountInfo, copyTradingSettingsData] = await Promise.all([
        fetchApi.get<IUTAMasterSettingsDetails>(ApiUrls.tradingAccountUser.getCopyTradingMasterAccount, {
          params: { id: masterId },
        }),
        getCopyTradingSettings(),
      ]);

      const account = accountInfo.data;
      const { recommendedMinDepositAmount } = account;
      const { profitSharingLimit, recommendedMinDepositLimits, isUpdateMasterLeverageAllowed } =
        copyTradingSettingsData.data;

      this.setState(
        {
          account,
          processing: false,
          profitSharingLimit,
          recommendedMinDepositAmount,
          recommendedMinDepositLimits,
          isUpdateMasterLeverageAllowed,
        },
        () => {
          this.formInitialization();
          this.setFormFieldsValues();

          this.formRef.current?.update(this.fields);
        },
      );
    } catch (error) {
      handleError(keyMap.sharedErrorsFailedToGetRecord, error);
      this.setState({ processing: false, errMsg: error.response.data });
    }
  };

  formInitialization = () => {
    const { account, isUpdateMasterLeverageAllowed } = this.state;
    const leveragesOption = account.leverages.map((leverage) => ({ value: leverage }));

    leverageFieldHandler(account.leverage, this.fields, isUpdateMasterLeverageAllowed, leveragesOption);
  };

  onChange = (name: string, value: string | boolean): void => {
    const { form } = this.state;

    form[name] = value;

    this.setState({ form });
  };

  makeDepositReqTriggerHandler = (value: boolean): void => {
    const depositAmountIdx = FormUtils.getFieldIndex(
      this.fields,
      EnumUTACopyTAMasterFormField.recommendedMinDepositAmount,
    );

    FormUtils.updateFormField(depositAmountIdx, { required: value }, this.fields);
    this.setDepositAmountTooltipMessage(value);
    this.resetFormValidation();
  };

  resetFormValidation = (): void => {
    if (this.formRef && this.formRef.current) {
      this.formRef.current.resetValidation();
    }
  };

  masterStatuses = (): IUTAMasterSettingsStatus[] => {
    const { i18next } = this.props;

    return [
      { isVisible: true, name: i18next.t(keyMap.tradingAccountsMasterStatusVisible) },
      { isVisible: false, name: i18next.t(keyMap.tradingAccountsMasterStatusInvisible) },
    ];
  };

  setFormFieldsValues = (): void => {
    const { account, recommendedMinDepositAmount } = this.state;

    const { accountTypeDisplayName, copyType, profitSharing, isVisible, commissionAccountId } = account;

    const commissionAccount = account.commissionAccounts?.find((account) => account.id === commissionAccountId);
    const { makeMinDepositAmountRequired } = account;
    const status = this.masterStatuses().find((status) => status.isVisible === isVisible)?.name;

    this.setStatus();

    this.setCommissionAccount();

    this.setProfitSharingLimit();

    this.setRecommendedDepositField(makeMinDepositAmountRequired);

    this.formStateSetter({
      accountType: accountTypeDisplayName,
      copyType,
      isVisible: status,
      profitSharing,
      commissionAccountLogin: commissionAccount?.login,
      recommendedMinDepositAmount,
      makeMinDepositAmountRequired,
      leverage: account.leverage,
    });
  };

  setStatus = (): void => {
    const statusIdx = FormUtils.getFieldIndex(this.fields, EnumUTACopyTAMasterFormField.isVisible);
    const statusOptions = this.masterStatuses().map((status) => ({ value: status.name }));

    if (statusIdx > -1) {
      FormUtils.updateFormField(statusIdx, { options: statusOptions }, this.fields);
    }
  };

  setCommissionAccount = (): void => {
    const { account } = this.state;
    const { commissionAccounts } = account;

    const commissionAccount = FormUtils.getFieldIndex(this.fields, EnumUTACopyTAMasterFormField.commissionAccountLogin);

    const commissionAccountOptions = commissionAccounts
      ? commissionAccounts.map((commissionAccounts) => ({ value: commissionAccounts.login }))
      : [];

    if (commissionAccount > -1) {
      FormUtils.updateFormField(commissionAccount, { options: commissionAccountOptions }, this.fields);
    }
  };

  setProfitSharingLimit = (): void => {
    const { profitSharingLimit } = this.state;

    const idx = FormUtils.getFieldIndex(this.fields, EnumUTACopyTAMasterFormField.profitSharing);

    if (idx > -1) {
      FormUtils.updateFormField(idx, { maxValue: profitSharingLimit }, this.fields);
    }
  };

  setRecommendedDepositField = (isDepositRequired: boolean): void => {
    const { recommendedMinDepositLimits } = this.state;
    const { groupMappingType } = this.props;

    const depositAmount = FormUtils.getFieldIndex(
      this.fields,
      EnumUTACopyTAMasterFormField.recommendedMinDepositAmount,
    );
    const makeDepositAmountRequired = FormUtils.getFieldIndex(
      this.fields,
      EnumUTACopyTAMasterFormField.makeMinDepositAmountRequired,
    );
    const isVisible = minDepositLimitsAreConfigured(recommendedMinDepositLimits, groupMappingType);

    if (isVisible) {
      FormUtils.updateFormField(depositAmount, { isHidden: false, required: isDepositRequired }, this.fields);
      FormUtils.updateFormField(makeDepositAmountRequired, { isHidden: false }, this.fields);

      this.setDepositAmountTooltipMessage(isDepositRequired);

      return;
    }

    FormUtils.updateFormField(depositAmount, { isHidden: true, required: false }, this.fields);
    FormUtils.updateFormField(makeDepositAmountRequired, { isHidden: true }, this.fields);

    return;
  };

  setDepositAmountTooltipMessage = (makeMinDepositAmountRequired: boolean): void => {
    const { recommendedMinDepositLimits, account } = this.state;
    const { groupMappingType } = this.props;
    const { currency } = account;

    const message = getMasterRecommendedDepositTooltipMsg(
      recommendedMinDepositLimits,
      currency,
      makeMinDepositAmountRequired,
      groupMappingType,
    );

    setTooltipMessage(
      FormUtils.getFieldIndex(this.fields, EnumUTACopyTAMasterFormField.recommendedMinDepositAmount),
      message,
      this.fields,
    );
  };

  findCommissionAccountId = (): number | undefined => {
    const { form, account } = this.state;

    const commissionAccountId = account.commissionAccounts.find(
      (account) => account.login === form.commissionAccountLogin,
    );

    return commissionAccountId?.id;
  };

  findMasterStatusValue = (): boolean | undefined => {
    const { form } = this.state;
    const masterStatuses = this.masterStatuses();

    const statusValue = masterStatuses.find((status) => status.name === form.isVisible);

    return statusValue?.isVisible;
  };

  formStateSetter = (data: IUTAMAFormAccountNewSettings): void => {
    const { form } = this.state;

    this.setState({ form: { ...form, ...data } });
  };

  render(): JSX.Element {
    const { form, processing, errMsg } = this.state;

    if (processing) {
      return <Preloader />;
    }

    if (errMsg) {
      return <ErrorView error={errMsg} />;
    }

    const actions = [
      {
        title: keyMap.tradingAccountsChangeSettings,
        onClick: this.updateMasterAccountData,
        type: 'submit',
        disabled: false,
      },
    ];
    // const Form = this.formComp;

    return (
      <Form
        {...this.props}
        className="form-box form-box--columns form--social"
        form={form}
        actions={actions}
        ref={this.formRef}
        makeDepositReqTrigger={this.makeDepositReqTriggerHandler}
        onChange={this.onChange}
      />
    );
  }
}
const MasterSettingsFormWithContext = connectToContext(MasterSettingsForm);

export { MasterSettingsFormWithContext as MasterSettingsForm };
